/**
 * Service zum Speichern und Laden von Standort-spezifischen Navigations-States
 * im localStorage.
 */

export interface StandortState {
  pathname: string;
  search?: string; // Query-Parameter
  state?: Record<string, any>; // Zusätzliche State-Werte (z.B. selectedTeacher)
}

const STORAGE_PREFIX = 'standortState_';
const SELECTED_STANDORT_PREFIX = 'selectedStandort_';

/**
 * Speichert den State für einen bestimmten Standort
 */
export function saveStandortState(standortId: number, state: StandortState): void {
  try {
    const key = `${STORAGE_PREFIX}${standortId}`;
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.warn('Fehler beim Speichern des Standort-States:', error);
  }
}

/**
 * Lädt den gespeicherten State für einen bestimmten Standort
 */
export function loadStandortState(standortId: number): StandortState | null {
  try {
    const key = `${STORAGE_PREFIX}${standortId}`;
    const stored = localStorage.getItem(key);
    if (!stored) {
      return null;
    }
    return JSON.parse(stored) as StandortState;
  } catch (error) {
    console.warn('Fehler beim Laden des Standort-States:', error);
    return null;
  }
}

/**
 * Löscht den gespeicherten State für einen bestimmten Standort
 */
export function clearStandortState(standortId: number): void {
  try {
    const key = `${STORAGE_PREFIX}${standortId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Fehler beim Löschen des Standort-States:', error);
  }
}

/**
 * Speichert einen einzelnen State-Wert für einen Standort
 */
export function saveStandortStateValue(standortId: number, key: string, value: any): void {
  const currentState = loadStandortState(standortId) || { pathname: '' };
  if (!currentState.state) {
    currentState.state = {};
  }
  currentState.state[key] = value;
  saveStandortState(standortId, currentState);
}

/**
 * Lädt einen einzelnen State-Wert für einen Standort
 */
export function loadStandortStateValue(standortId: number, key: string): any {
  const state = loadStandortState(standortId);
  return state?.state?.[key] ?? null;
}

/**
 * Speichert den zuletzt ausgewählten Standort pro Benutzer.
 */
export function saveSelectedStandortId(userId: string, standortId: number): void {
  try {
    const key = `${SELECTED_STANDORT_PREFIX}${userId}`;
    localStorage.setItem(key, String(standortId));
  } catch (error) {
    console.warn('Fehler beim Speichern des ausgewählten Standorts:', error);
  }
}

/**
 * Lädt den zuletzt ausgewählten Standort pro Benutzer.
 */
export function loadSelectedStandortId(userId: string): number | null {
  try {
    const key = `${SELECTED_STANDORT_PREFIX}${userId}`;
    const stored = localStorage.getItem(key);
    if (!stored) {
      return null;
    }

    const parsed = Number(stored);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  } catch (error) {
    console.warn('Fehler beim Laden des ausgewählten Standorts:', error);
    return null;
  }
}

/**
 * Löscht den gespeicherten ausgewählten Standort für einen Benutzer.
 */
export function clearSelectedStandortId(userId: string): void {
  try {
    const key = `${SELECTED_STANDORT_PREFIX}${userId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Fehler beim Löschen des ausgewählten Standorts:', error);
  }
}

